<template>
  <div>
    <!-- Summary Header -->
    <div class="d-flex align-items-center mb-3">
      <h5 class="preview-summary mb-0 me-3">
        {{ totalEntries }} Entries {{ isExistingBatch ? 'Found' : 'Uploaded' }}
      </h5>
      <span class="summary-valid me-3">• {{ validEntries }} Valid</span>
      <span class="summary-error">• {{ errorEntries }} Error</span>
    </div>

    <!-- Error Alert -->
    <div
      v-if="errorEntries > 0"
      class="alert error-alert d-flex align-items-center p-3 mb-4"
      role="alert"
    >
      <i class="fas fa-exclamation-triangle me-2 text-danger"></i>
      <div>
        There's {{ errorEntries }} error data, please click "Fix error" button
        before you continue
      </div>
    </div>

    <!-- Data Table -->
    <SwiftDataTable
      :headers="tableHeaders"
      :items="tableData"
      :items-per-page="20"
      :show-select-all="true"
      show-index
      buttons-pagination
      v-model:items-selected="selectedTableItems"
      :header-item-class-name="headerItemClassNameFunction"
      :body-item-class-name="bodyItemClassNameFunction"
    >
      <template #item-status="{ status }">
        <span
          :class="['badge', status === 'Success' || status === 'Draft' ? 'bg-success' : 'bg-danger']"
        >
          {{ status == "Success" || status == "Draft" ? "Valid" : "Error" }}
        </span>
      </template>

      <template #item-actions="{ item, index }">
        <div class="d-flex gap-2">
          <img
            class="cursor-pointer"
            src="../../../../public/assets/icons/ic_edit.svg"
            @click="editEntry(index - 1, item)"
            width="16px"
            height="16px"
            alt="Edit Icon"
          />
          <img
            class="cursor-pointer"
            src="../../../../public/assets/icons/ic_delete.svg"
            @click="deleteEntry(index - 1)"
            width="16px"
            height="16px"
            alt="Delete Icon"
          />
        </div>
      </template>

      <template #item-account_name="{ account_name, error_message }">
        <div class="account-name-col">
          {{ account_name }}
          <br />
          <small
            v-if="
              error_message &&
              error_message.toUpperCase().includes('ACCOUNT NAME')
            "
            style="color: red; font-size: 0.75rem; margin-top: 2px"
            >{{ error_message }}</small
          >
        </div>
      </template>

      <template #item-account_number="{ account_number, error_message }">
        <div class="account-number-col">
          {{ account_number }}
          <br />
          <small
            v-if="
              error_message &&
              error_message.toUpperCase().includes('ACCOUNT NUMBER')
            "
            style="color: red; font-size: 0.75rem; margin-top: 2px"
            >{{ error_message }}</small
          >
        </div>
      </template>

      <template #item-amount="{ amount, error_message }">
        <template
          v-if="
            typeof amount === 'number' ||
            (typeof amount === 'string' && !isNaN(parseFloat(amount)))
          "
        >
          IDR {{ formatLocalNumber(amount) }}
          <br />
          <small
            v-if="
              error_message && error_message.toUpperCase().includes('AMOUNT')
            "
            style="color: red; font-size: 0.75rem; margin-top: 2px"
            >{{ error_message }}</small
          >
        </template>
        <template velse>
          {{ amount }}
          <br />
          <small
            v-if="
              error_message && error_message.toUpperCase().includes('AMOUNT')
            "
            style="color: red; font-size: 0.75rem; margin-top: 2px"
            >{{ error_message }}</small
          >
        </template>
      </template>

      <template #item-bank="{ bank, error_message }">
        <div class="bank-col">
          {{ bank }}
          <br />
          <small
            v-if="error_message && error_message.toUpperCase().includes('BANK')"
            style="color: red; font-size: 0.75rem; margin-top: 2px"
            >{{ error_message }}</small
          >
        </div>
      </template>

      <template #item-admin_fee="{ admin_fee }">
        IDR {{ formatLocalNumber(admin_fee) }}
      </template>

      <template #item-uuid="{ uuid }">
        {{ uuid }}
      </template>
    </SwiftDataTable>

    <!-- Bottom Actions -->
    <div class="d-flex justify-content-end align-items-center mt-4 gap-2">
      <ArgonButton
        v-if="!isExistingBatch && errorEntries > 0"
        variant="export"
        @click="onContinueSkipErrors"
      >
        Continue & Skip Error
      </ArgonButton>
      <ArgonButton 
        color="danger" 
        variant="outline" 
        @click="onCancelBatch"
      >
        Cancel
      </ArgonButton>
      <ArgonButton color="success" @click="onFixErrors">
        {{ isExistingBatch 
          ? (errorEntries === 0 ? "Continue" : "View Errors") 
          : (errorEntries === 0 ? "Continue" : "Fix Error") 
        }}
      </ArgonButton>
    </div>

    <!-- Fix Error Modal -->
    <FixErrorModal
      :show="showFixErrorModal"
      :error-item="currentErrorToFix"
      :error-index="currentErrorIndex"
      :total-errors="allErrorItems.length"
      :batchUUID="props.batchUUID"
      @close="handleCloseModal"
      @save-error="handleSaveFixedError"
      @skip-error="handleSkipError"
    />
  </div>
</template>

<script setup>
import { ref, watch, nextTick, defineProps, defineEmits } from "vue";
import ArgonButton from "@/components/ArgonButton.vue";
import SwiftDataTable from "@/components/SwiftDataTable.vue";
import Swal from "sweetalert2"; // Import Swal directly
// import EasyDataTable from 'vue3-easy-data-table';
// import 'vue3-easy-data-table/dist/style.css'; // Import base styles here
import FixErrorModal from "./FixErrorModal.vue"; // New import
import { getDataBatchFile, deleteBatchfileByRow, cancelBatch } from "@/api/ppob"; // Add cancelBatch import
// import { detailBatch } from "@/api/transaction"; // Keep for potential future use, but currently using getDataBatchFile for existing batch

const props = defineProps({
  batchUUID: String, // New prop for the batch UUID
  fileName: String, // To display batch name if needed
  isExistingBatch: { // New prop to indicate if this is an existing batch (from transaction list)
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  "update:isProcessing", // For parent to control global modal
  "processing-failed",
  "back-to-upload", // New: To go back to step 1
  "proceed-to-review", // New: To go to step 3
]);

const tableHeaders = ref([
  {
    text: "No",
    value: "index",
    fixed: true,
    sortable: false,
    width: 40,
    stickyColumn: true,
  },
  {
    text: "Status",
    value: "status",
    fixed: true,
    sortable: false,
    width: 40,
    stickyColumn: true,
  },
  {
    text: "Action",
    value: "actions",
    fixed: true,
    sortable: false,
    width: 50,
    stickyColumn: true,
  },
  {
    text: "Account Name",
    value: "account_name",
    fixed: true,
    sortable: false,
    width: 150,
    stickyColumn: false,
  },
  {
    text: "Account Number",
    value: "account_number",
    fixed: true,
    sortable: false,
    width: 150,
    stickyColumn: false,
  },
  {
    text: "Amount",
    value: "amount",
    fixed: true,
    sortable: false,
    width: 100,
    stickyColumn: false,
  },
  {
    text: "Product Name",
    value: "bank",
    fixed: true,
    sortable: false,
    width: 100,
    stickyColumn: false,
  },
  {
    text: "Admin Fee",
    value: "admin_fee",
    fixed: true,
    sortable: false,
    width: 100,
    stickyColumn: false,
  },
  // ...existing code...
]);
const tableData = ref([]);
const totalEntries = ref(0);
const validEntries = ref(0);
const errorEntries = ref(0);

// State for Fix Error Modal
const showFixErrorModal = ref(false);
const currentErrorToFix = ref(null);
const currentErrorIndex = ref(0);
const allErrorItems = ref([]);
const isEditingSingleItem = ref(false);
const selectedTableItems = ref([]); // New ref for selected items from SwiftDataTable

// Function to add class to header items
const headerItemClassNameFunction = (header) => {
  if (header.value === "uuid") return "hidden-uuid-column";
  return "";
};

// Function to add class to body items
const bodyItemClassNameFunction = (column) => {
  // 'column' here refers to the header.value for that cell's column
  if (column === "uuid") return "hidden-uuid-column";
  return "";
};

// Process uploaded file
const fetchAndProcessBatchData = async (uuid) => {
  if (!uuid) return;

  emit("update:isProcessing", true);
  let processingSuccess = false;

  try {
    let response;
    let dataFromFile = [];
    let headers = [];

    // Use different API based on whether this is existing batch or new upload
    if (props.isExistingBatch) {
      // For existing batch from transaction list, use getDataBatchFile API
      // This API returns: {data: {batch: {transactions: {data: [...], pagination: {...}}}}}
      response = await getDataBatchFile(uuid);
      
      if (response && response.status === "success" && response.data) {

        if (response.data.batch && response.data.transactions.data) {
          const rawTransactions = response.data.transactions.data;
          
          const transactions = rawTransactions.map((transaction, index) => ({
            ...transaction,
            // Ensure UUID field exists untuk konsistensi dengan struktur yang diharapkan
            uuid: transaction.uuid || transaction.id || transaction.transaction_id || `temp_${index}`,
            // Map status dari existing batch format ke expected format  
            status: transaction.status === 'Success' || transaction.status === 'success' || transaction.status === 'Draft' ? 'Success' : 'Failed',
            error_message: transaction.error_message || transaction.notes || ""
          }));
          dataFromFile = transactions;
        } else if (response.data.batch && response.data.transactions && Array.isArray(response.data.transactions)) {
          // Alternative structure: transactions might be direct array
          const rawTransactions = response.data.batch.transactions;
          
          const transactions = rawTransactions.map((transaction, index) => ({
            ...transaction,
            uuid: transaction.uuid || transaction.id || transaction.transaction_id || `temp_${index}`,
            status: transaction.status === 'Success' || transaction.status === 'success' ? 'Success' : 'Failed',
            error_message: transaction.error_message || transaction.notes || ""
          }));
          dataFromFile = transactions;
        } else {
          console.error("Missing transaction data in response:", response.data);
          throw new Error("Transaction data not found in API response");
        }
      } else {
        console.error("Unexpected response structure from getDataBatchFile:", response);
        throw new Error("Invalid response structure from batch API");
      }
    } else {
      // For new upload, use existing getDataBatchFile API
      response = await getDataBatchFile(uuid);

      if (response && response.data && response.data.transactions && response.data.transactions.data) {
        const transactions = response.data.transactions.data.map((transaction) => ({
          ...transaction,
        }));
        if (Array.isArray(transactions)) {
          dataFromFile = transactions.map((entry) => {
            let errorMessageString = "";
            if (entry.error_message && typeof entry.error_message === "object") {
              errorMessageString = Object.values(entry.error_message).join(", ");
            } else if (typeof entry.error_message === "string") {
              errorMessageString = entry.error_message;
            }
            return {
              ...entry, // Spread all original fields from API
              error_message: errorMessageString, // Standardized error message string
            };
          });
        } else {
          throw new Error("Transactions data is not an array.");
        }
      } else {
        throw new Error("Invalid data structure from getDataBatchFile API or no data received.");
      }
    }

    headers = [
      "account_name",
      "account_number", 
      "amount",
      "bank",
      "admin_fee",
      "uuid", // Kembali ke UUID untuk konsistensi
    ];
    const dynamicHeaders = headers.map((header) => ({
      text: header.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()), // Format header text
      value: header,
      sortable: true,
    }));

    tableHeaders.value = [
      {
        text: "Status",
        value: "status",
        fixed: true,
        sortable: false,
        width: 65,
      },
      {
        text: "Action",
        value: "actions",
        fixed: true,
        sortable: false,
        width: 60,
      },
      ...dynamicHeaders,
    ];
    tableData.value = dataFromFile;
    totalEntries.value = dataFromFile.length;
    // Adjust status checking based on API's values (e.g., 'Success', 'Failed')
    validEntries.value = dataFromFile.filter(
      (item) => item.status === "Success" || item.status === "Draft"
    ).length;
    errorEntries.value = dataFromFile.filter(
      (item) => item.status === "Failed"
    ).length;

    processingSuccess = true;
  } catch (error) {
    console.error("Error fetching or processing batch data:", error);
    console.error("Error details:", {
      message: error.message,
      stack: error.stack,
      uuid: uuid,
      isExistingBatch: props.isExistingBatch
    });
    processingSuccess = false;
    tableHeaders.value = [];
    tableData.value = [];
    totalEntries.value = 0;
    validEntries.value = 0;
    errorEntries.value = 0;
    
    // Store error for finally block
    window.lastProcessingError = error;
  } finally {
    emit("update:isProcessing", false);
    await nextTick();
    if (!processingSuccess) {
      const errorMessage = window.lastProcessingError?.message || 'Unknown error';
      emit(
        "processing-failed",
        `Failed to load or process batch data from server. ${errorMessage}`
      );
      // Clean up
      delete window.lastProcessingError;
    }
  }
};

watch(
  () => props.batchUUID,
  (newUUID) => {
    if (newUUID) {
      fetchAndProcessBatchData(newUUID);
    } else {
      // Reset local state if file is removed/nulled by parent
      tableHeaders.value = [];
      tableData.value = [];
      totalEntries.value = 0;
      validEntries.value = 0;
      errorEntries.value = 0;
      // Reset modal related states as well
      showFixErrorModal.value = false;
      currentErrorToFix.value = null;
      currentErrorIndex.value = 0;
      allErrorItems.value = [];
      selectedTableItems.value = []; // Reset selected items
    }
  },
  { immediate: true }
);

// --- Internal Action Handlers ---
const editEntry = (rowIndex, item = null) => {
  // For existing batch, editing is not allowed  
  if (props.isExistingBatch) {
    Swal.fire("Info", "Cannot edit entries from existing batch. This data is already processed.", "info");
    return;
  }

  const itemToEdit = tableData.value[rowIndex];

  if (!itemToEdit) {
    console.error("Item not found at index:", rowIndex, item);
    return;
  }

  selectedTableItems.value = []; // Clear selections when editing a single item directly
  allErrorItems.value = [itemToEdit];
  currentErrorToFix.value = { ...itemToEdit };
  currentErrorIndex.value = rowIndex;
  isEditingSingleItem.value = true;
  showFixErrorModal.value = true;
};

const deleteEntry = async (rowIndex) => {
  // For existing batch, deletion is not allowed
  if (props.isExistingBatch) {
    Swal.fire("Info", "Cannot delete entries from existing batch. This data is already processed.", "info");
    return;
  }

  try {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      const itemToDelete = tableData.value[rowIndex];

      // Use UUID for both new uploads and existing batch (setelah mapping)
      const itemId = itemToDelete.uuid;
      if (!itemToDelete || !itemId) {
        Swal.fire("Error!", "Invalid item data for deletion.", "error");
        return;
      }

      const response = await deleteBatchfileByRow(itemId);
      if (response && response.status == "success") {
        // Check for success based on your API response structure
        tableData.value = tableData.value.filter(
          (entry) => entry.uuid !== itemId
        );
        validEntries.value = tableData.value.filter(
          (entry) => entry.status === "Success"
        ).length;
        errorEntries.value = tableData.value.filter(
          (entry) => entry.status === "Failed"
        ).length;
        selectedTableItems.value = selectedTableItems.value.filter(
          (selectedItem) => selectedItem.uuid !== itemId
        );
        Swal.fire("Deleted!", "The entry has been deleted.", "success");
      }
    }
  } catch (error) {
    console.error("Error deleting entry:", error);
    let errorMessage = "An unexpected error occurred while deleting the entry.";
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    }
    Swal.fire("Error!", errorMessage, "error");
  }
};


const onCancelBatch = async () => {
  try {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: props.isExistingBatch 
        ? "You want to cancel this batch transaction?" 
        : "You want to cancel this upload process?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
    });

    if (result.isConfirmed) {
      if (props.isExistingBatch) {
        // For existing batch, call cancel API
        // Show loading
        Swal.fire({
          title: "Cancelling...",
          text: "Please wait while we cancel the batch.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call cancel API
        const response = await cancelBatch(props.batchUUID);
        
        if (response && response.status === 'success') {
          Swal.fire({
            title: "Cancelled!",
            text: "Batch has been cancelled successfully.",
            icon: "success",
            confirmButtonText: "OK"
          }).then(() => {
            // Redirect to transaction page
            window.location.href = '/transaction';
          });
        } else {
          throw new Error(response?.message || 'Failed to cancel batch');
        }
      } else {
        // For new upload, just go back to upload step
        Swal.fire({
          title: "Cancelled!",
          text: "Upload process has been cancelled.",
          icon: "success",
          confirmButtonText: "OK"
        }).then(() => {
          emit("back-to-upload"); // Signal parent to go back to step 1
        });
      }
    }
  } catch (error) {
    console.error("Error cancelling batch:", error);
    let errorMessage = "An unexpected error occurred while cancelling the batch.";
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    Swal.fire("Error!", errorMessage, "error");
  }
};

const onFixErrors = () => {
  // For existing batch with errors, show info that errors cannot be fixed
  if (props.isExistingBatch && errorEntries.value > 0) {
    Swal.fire("Info", "This is an existing batch with failed transactions. Failed entries cannot be modified.", "info");
    return;
  }
  
  // For existing batch without errors, proceed to review
  if (props.isExistingBatch && errorEntries.value === 0) {
    emit("proceed-to-review", tableData.value);
    return;
  }

  // Original logic for new uploads
  isEditingSingleItem.value = false;

  let errorsToProcess = [];
  if (selectedTableItems.value.length > 0) {
    // Filter selected items to only include those with 'Failed' status
    errorsToProcess = selectedTableItems.value.filter(
      (item) => item.status === "Failed"
    );
    if (errorsToProcess.length === 0) {
      // If selection contains no 'Failed' items, but there are errors overall, revert to all errors
      // Or, you could show a message: "No selected items are errors."
      // For now, let's fall back to all errors if selection doesn't yield any failed items.
      errorsToProcess = tableData.value.filter(
        (item) => item.status === "Failed"
      );
    }
  } else {
    // If no items are selected, use all items with 'Failed' status
    errorsToProcess = tableData.value.filter(
      (item) => item.status === "Failed"
    );
  }

  allErrorItems.value = errorsToProcess;

  if (allErrorItems.value.length > 0) {
    currentErrorIndex.value = 0;
    currentErrorToFix.value = { ...allErrorItems.value[0] };
    showFixErrorModal.value = true;
  } else {
    // If there were selected items but none were errors, we might not want to proceed directly.
    // However, if errorEntries.value is 0, then it's fine to proceed.
    if (errorEntries.value === 0) {
      emit("proceed-to-review", tableData.value);
    } else if (
      selectedTableItems.value.length > 0 &&
      errorsToProcess.length === 0
    ) {
      Swal(
        "Info",
        "The selected items do not contain any entries with a 'Failed' status. Please select failed entries to fix or click 'Fix Error' without selection to fix all.",
        "info"
      );
    } else {
      // This case implies no errors at all, or some other logic branch.
      emit("proceed-to-review", tableData.value);
    }
    showFixErrorModal.value = false; // Ensure modal is closed if no errors to fix
  }
};

const handleCloseModal = () => {
  showFixErrorModal.value = false;
  currentErrorToFix.value = null;
  isEditingSingleItem.value = false;
  // Refresh allErrorItems based on current table data, not selection, as modal is closing.
  allErrorItems.value = tableData.value.filter(
    (item) => item.status === "Failed"
  );
  selectedTableItems.value = []; // Clear selection when modal is closed
};

const handleSaveFixedError = async () => {
  // Recalculate all data by refetching from the server
  // This ensures all counts and data are properly synchronized with the backend
  await fetchAndProcessBatchData(props.batchUUID);

  // Logic to move to the next error or close modal
  // Recalculate allErrorItems from the freshly fetched tableData
  allErrorItems.value = tableData.value.filter(
    (item) => item.status === "Failed"
  );

  if (
    !isEditingSingleItem.value &&
    allErrorItems.value.length > 0 &&
    currentErrorIndex.value < allErrorItems.value.length - 1
  ) {
    currentErrorIndex.value++;
    currentErrorToFix.value = {
      ...allErrorItems.value[currentErrorIndex.value],
    };
  } else {
    showFixErrorModal.value = false;
    currentErrorToFix.value = null;
    isEditingSingleItem.value = false;
    selectedTableItems.value = []; // Clear selection when done
  }
};

const onContinueSkipErrors = () => {
  emit("proceed-to-review", tableData.value); // Pass current data to parent if needed for next step
};

// Optional: Handle modal loading states if you want a global overlay
// const handleModalValidationStart = () => { emit('update:isProcessing', true); };
// const handleModalValidationComplete = () => { emit('update:isProcessing', false); };
const formatLocalNumber = (value) => {
  if (value === null || value === undefined) return "0";
  const num = Number(value);
  if (isNaN(num)) return value;
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
</script>

<style scoped>
/* Styles from original Transfer.vue related to Step 2 */
.preview-summary {
  color: #344767; /* Or your desired heading color */
}
.summary-valid {
  color: #2dce89; /* Argon success */
  font-size: 0.875rem;
}
.summary-error {
  color: #fb6340; /* Argon danger */
  font-size: 0.875rem;
}
.error-alert {
  background-color: #fdece8; /* Lighter shade for danger */
  border: 1px solid #fb6340;
  color: #fb6340;
  font-size: 0.875rem;
}
.error-alert i {
  font-size: 1.1rem;
}
.preview-table .action-icon {
  cursor: pointer;
  color: #5e72e4; /* Argon primary or a neutral color */
  transition: color 0.2s ease;
}
.preview-table .action-icon:hover {
  color: #324cdd; /* Darker shade of primary */
}
.preview-table .error-message {
  color: #fb6340; /* Argon danger */
  font-size: 0.75rem;
  margin-top: 2px;
}
.account-number-col .error-message {
  /* Specific styling if needed, otherwise general .error-message applies */
}
.badge.bg-success {
  background-color: #2dce89 !important;
}
.badge.bg-danger {
  background-color: #fb6340 !important;
}
.preview-table th.stickyColumn,
.preview-table td.stickyColumn {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: #fff;
  border-right: 1px solid #dee2e6;
}
.preview-table th.stickyColumn:nth-child(1),
.preview-table td.stickyColumn:nth-child(1) {
  left: 0; /* "No" column */
}
.preview-table th.stickyColumn:nth-child(2),
.preview-table td.stickyColumn:nth-child(2) {
  left: 40px; /* "Status" column */
}
.preview-table th.stickyColumn:nth-child(3),
.preview-table td.stickyColumn:nth-child(3) {
  left: 80px; /* "Action" column */
}
/* Adjust z-index for overlapping headers */
.preview-table th {
  z-index: 1;
}
.shadow {
  box-shadow: none !important;
}

/* Hide ID column */
.hidden-id-column {
  display: none !important;
}
/* Hide UUID column */
.hidden-uuid-column {
  display: none !important;
}
</style>
